/**
 * TzKT API client for fetching blockchain data
 * https://api.tzkt.io/ (mainnet) or https://api.ghostnet.tzkt.io/ (testnet)
 *
 * For sandbox mode, falls back to direct RPC queries if TzKT is unavailable.
 */

import {
  NFT,
  NFTListing,
  NFTMetadata,
  CollectionToken,
  PaginatedResponse,
  CollectionStats,
  TraitSummary,
  TraitIndex,
  TzktToken,
  TzktTokenMetadata,
} from './types';
import { getCollectionByAddress, getCurrentNetwork } from './collections';
import { ipfsToHttp, fetchIPFSMetadata } from './ipfs';

// Sandbox TzKT only available in development - use PROD check for proper dead code elimination
const SANDBOX_TZKT_URL = import.meta.env.PROD ? 'https://api.tzkt.io/v1' : 'http://localhost:5002/v1';

const getTzktApiUrl = (networkOverride?: 'mainnet' | 'ghostnet' | 'sandbox'): string => {
  const network = networkOverride || import.meta.env.VITE_NETWORK || 'ghostnet';

  // TzKT has CORS enabled, so we can call it directly from the browser
  switch (network) {
    case 'mainnet':
      return 'https://api.tzkt.io/v1';
    case 'ghostnet':
      return 'https://api.ghostnet.tzkt.io/v1';
    case 'sandbox':
      return import.meta.env.VITE_TZKT_URL || SANDBOX_TZKT_URL;
    default:
      return 'https://api.tzkt.io/v1';
  }
};

// Sandbox RPC only available in development - use PROD check for proper dead code elimination
const SANDBOX_RPC_URL = import.meta.env.PROD ? 'https://mainnet.api.tez.ie' : 'http://localhost:20000';

const getRpcUrl = (): string => {
  // In browser, use proxy to avoid CORS issues
  if (typeof window !== 'undefined') {
    return '/rpc';
  }
  return import.meta.env.VITE_RPC_URL || SANDBOX_RPC_URL;
};

/**
 * Decode hex string to UTF-8 (browser-compatible, no Buffer needed)
 */
const hexToUtf8 = (hex: string): string => {
  try {
    // Remove '0x' prefix if present
    const cleanHex = hex.startsWith('0x') ? hex.slice(2) : hex;
    // Convert hex pairs to bytes and decode as UTF-8
    const bytes = new Uint8Array(cleanHex.length / 2);
    for (let i = 0; i < cleanHex.length; i += 2) {
      bytes[i / 2] = parseInt(cleanHex.substring(i, i + 2), 16);
    }
    return new TextDecoder('utf-8').decode(bytes);
  } catch (e) {
    console.warn('Failed to decode hex:', e);
    return '';
  }
};

interface TzktTokenBalance {
  id: number;
  account: {
    address: string;
  };
  token: {
    id: number;
    contract: {
      address: string;
    };
    tokenId: string;
    metadata?: {
      name?: string;
      description?: string;
      displayUri?: string;
      artifactUri?: string;
      thumbnailUri?: string;
      image?: string;
      attributes?: Array<{ name: string; value: string }>;
    };
  };
  balance: string;
}

interface TzktBigMapEntry {
  id: number;
  active: boolean;
  key: string;
  value: {
    seller?: string;
    token_address?: string;
    token_id?: string;
    price?: string;
    active?: boolean;
  };
}

/**
 * Fetch all NFTs owned by a specific address
 */
export const fetchNFTsByOwner = async (ownerAddress: string): Promise<NFT[]> => {
  const apiUrl = getTzktApiUrl();

  try {
    const response = await fetch(
      `${apiUrl}/tokens/balances?account=${ownerAddress}&balance.gt=0&token.standard=fa2&limit=50`
    );

    if (!response.ok) {
      throw new Error(`TzKT API error: ${response.status}`);
    }

    const balances: TzktTokenBalance[] = await response.json();

    const nfts: NFT[] = await Promise.all(
      balances.map(async (balance) => {
        const tokenMetadata = balance.token.metadata;

        // Try to get image from various metadata fields
        let image = tokenMetadata?.displayUri ||
          tokenMetadata?.artifactUri ||
          tokenMetadata?.thumbnailUri ||
          tokenMetadata?.image ||
          '';

        // If metadata has a URI that needs fetching, get it
        if (!tokenMetadata && balance.token.tokenId) {
          // Could fetch from token metadata URI if needed
        }

        const metadata: NFTMetadata = {
          name: tokenMetadata?.name || `Token #${balance.token.tokenId}`,
          description: tokenMetadata?.description || '',
          image,
          attributes: tokenMetadata?.attributes?.map(attr => ({
            trait_type: attr.name,
            value: attr.value,
          })),
        };

        return {
          tokenAddress: balance.token.contract.address,
          tokenId: parseInt(balance.token.tokenId),
          owner: balance.account.address,
          metadata,
        };
      })
    );

    return nfts;
  } catch (error) {
    console.error('Failed to fetch NFTs from TzKT:', error);
    return [];
  }
};

/**
 * Fetch active listings from the marketplace contract using Taquito (sandbox fallback)
 */
const fetchListingsFromTaquito = async (): Promise<NFTListing[]> => {
  const rpcUrl = getRpcUrl();
  const contractAddress = import.meta.env.VITE_MARKETPLACE_CONTRACT;

  if (!contractAddress) {
    return [];
  }

  try {
    // Dynamic import Taquito to avoid issues with SSR
    const { TezosToolkit } = await import('@taquito/taquito');
    const tezos = new TezosToolkit(rpcUrl);
    const contract = await tezos.contract.at(contractAddress);
    const storage = await contract.storage() as {
      next_listing_id: { toNumber: () => number };
      listings: { get: (id: number) => Promise<{
        active: boolean;
        price: { toNumber: () => number };
        seller: string;
        token_address: string;
        token_id: { toNumber: () => number };
      } | undefined> };
    };

    const nextListingId = storage.next_listing_id.toNumber();
    const listings: NFTListing[] = [];

    // Fetch each listing from the big_map
    for (let i = 0; i < nextListingId; i++) {
      const listing = await storage.listings.get(i);
      if (listing && listing.active) {
        listings.push({
          id: i,
          seller: listing.seller,
          tokenAddress: listing.token_address,
          tokenId: listing.token_id.toNumber(),
          price: listing.price.toNumber().toString(),
          active: listing.active,
        });
      }
    }

    return listings;
  } catch (error) {
    console.error('Failed to fetch listings from Taquito:', error);
    return [];
  }
};

/**
 * Fetch active listings from the marketplace contract
 */
export const fetchMarketplaceListings = async (): Promise<NFTListing[]> => {
  const apiUrl = getTzktApiUrl();
  const contractAddress = import.meta.env.VITE_MARKETPLACE_CONTRACT;
  const network = import.meta.env.VITE_NETWORK || 'ghostnet';

  if (!contractAddress) {
    console.warn('Marketplace contract address not configured');
    return [];
  }

  // For sandbox, always use Taquito directly (TzKT may not index marketplace contract properly)
  if (network === 'sandbox') {
    console.log('Sandbox mode: using Taquito for listings');
    return fetchListingsFromTaquito();
  }

  try {
    // Fetch the listings big_map from the contract storage
    const response = await fetch(
      `${apiUrl}/contracts/${contractAddress}/bigmaps/listings/keys?active=true&limit=100`
    );

    if (!response.ok) {
      throw new Error(`TzKT API error: ${response.status}`);
    }

    const entries: TzktBigMapEntry[] = await response.json();

    const listings: NFTListing[] = entries
      .filter(entry => entry.active && entry.value.active)
      .map(entry => ({
        id: parseInt(entry.key),
        seller: entry.value.seller || '',
        tokenAddress: entry.value.token_address || '',
        tokenId: parseInt(entry.value.token_id || '0'),
        price: entry.value.price || '0',
        active: entry.value.active || false,
      }));

    return listings;
  } catch (error) {
    console.error('Failed to fetch listings from TzKT:', error);
    // Fallback to Taquito for sandbox
    if (network === 'sandbox') {
      return fetchListingsFromTaquito();
    }
    return [];
  }
};

/**
 * Fetch NFTs that are listed on the marketplace with their metadata
 */
export const fetchListedNFTs = async (): Promise<NFT[]> => {
  const apiUrl = getTzktApiUrl();
  const listings = await fetchMarketplaceListings();

  if (listings.length === 0) {
    return [];
  }

  try {
    // Fetch token metadata for each listing
    const nfts: NFT[] = await Promise.all(
      listings.map(async (listing) => {
        try {
          // Fetch token info from TzKT
          const tokenResponse = await fetch(
            `${apiUrl}/tokens?contract=${listing.tokenAddress}&tokenId=${listing.tokenId}&limit=1`
          );

          let metadata: NFTMetadata = {
            name: `Token #${listing.tokenId}`,
            description: '',
            image: '',
          };

          if (tokenResponse.ok) {
            const tokens = await tokenResponse.json();
            if (tokens.length > 0 && tokens[0].metadata) {
              const tokenMeta = tokens[0].metadata;
              metadata = {
                name: tokenMeta.name || metadata.name,
                description: tokenMeta.description || '',
                image: tokenMeta.displayUri || tokenMeta.artifactUri || tokenMeta.image || '',
                attributes: tokenMeta.attributes?.map((attr: { name: string; value: string }) => ({
                  trait_type: attr.name,
                  value: attr.value,
                })),
              };
            }
          }

          return {
            tokenAddress: listing.tokenAddress,
            tokenId: listing.tokenId,
            owner: listing.seller,
            metadata,
            listing,
          };
        } catch {
          // Return listing without full metadata if fetch fails
          return {
            tokenAddress: listing.tokenAddress,
            tokenId: listing.tokenId,
            owner: listing.seller,
            metadata: {
              name: `Token #${listing.tokenId}`,
              description: '',
              image: '',
            },
            listing,
          };
        }
      })
    );

    return nfts;
  } catch (error) {
    console.error('Failed to fetch listed NFTs:', error);
    return [];
  }
};

/**
 * Fetch a single NFT by contract address and token ID
 */
export const fetchNFTDetails = async (
  tokenAddress: string,
  tokenId: string, // String to preserve large integer precision
  network?: 'mainnet' | 'ghostnet' | 'sandbox'
): Promise<NFT | null> => {
  const apiUrl = getTzktApiUrl(network);

  try {
    // Fetch token info
    const tokenResponse = await fetch(
      `${apiUrl}/tokens?contract=${tokenAddress}&tokenId=${tokenId}&limit=1`
    );

    if (!tokenResponse.ok) {
      throw new Error(`TzKT API error: ${tokenResponse.status}`);
    }

    const tokens = await tokenResponse.json();

    if (tokens.length === 0) {
      return null;
    }

    const token = tokens[0];

    // Fetch current holder
    const balanceResponse = await fetch(
      `${apiUrl}/tokens/balances?token.contract=${tokenAddress}&token.tokenId=${tokenId}&balance.gt=0&limit=1`
    );

    let owner = '';
    if (balanceResponse.ok) {
      const balances = await balanceResponse.json();
      if (balances.length > 0) {
        owner = balances[0].account.address;
      }
    }

    // Check if listed on marketplace
    let listing: NFTListing | undefined;
    const contractAddress = import.meta.env.VITE_MARKETPLACE_CONTRACT;

    if (contractAddress) {
      const listings = await fetchMarketplaceListings();
      listing = listings.find(
        l => l.tokenAddress === tokenAddress && l.tokenId === tokenId && l.active
      );
    }

    const tokenMeta = token.metadata || {};

    return {
      tokenAddress,
      tokenId,
      owner,
      metadata: {
        name: tokenMeta.name || `Token #${tokenId}`,
        description: tokenMeta.description || '',
        image: tokenMeta.displayUri || tokenMeta.artifactUri || tokenMeta.image || '',
        attributes: tokenMeta.attributes?.map((attr: { name: string; value: string }) => ({
          trait_type: attr.name,
          value: attr.value,
        })),
      },
      listing,
    };
  } catch (error) {
    console.error('Failed to fetch NFT details:', error);
    return null;
  }
};

/**
 * Check if TzKT API is available
 */
export const isTzktAvailable = async (): Promise<boolean> => {
  const apiUrl = getTzktApiUrl();

  try {
    const response = await fetch(`${apiUrl}/head`, { method: 'GET' });
    return response.ok;
  } catch {
    return false;
  }
};

// ============================================================================
// Collection Browser Functions
// ============================================================================

/**
 * Helper to map TzKT metadata to our NFTMetadata format
 */
const mapTzktMetadata = (tzktMeta?: TzktTokenMetadata): NFTMetadata => ({
  name: tzktMeta?.name || 'Unnamed',
  description: tzktMeta?.description || '',
  image: tzktMeta?.displayUri || tzktMeta?.artifactUri || tzktMeta?.thumbnailUri || tzktMeta?.image || '',
  attributes: tzktMeta?.attributes?.map(attr => ({
    trait_type: attr.name,
    value: attr.value,
  })),
});

/**
 * Fetch tokens from a collection with pagination
 */
export const fetchCollectionTokens = async (
  contractAddress: string,
  options: {
    offset?: number;
    limit?: number;
    sort?: 'tokenId' | 'lastTime';
    sortDir?: 'asc' | 'desc';
  } = {}
): Promise<PaginatedResponse<CollectionToken>> => {
  const apiUrl = getTzktApiUrl();
  const { offset = 0, limit = 24, sort = 'tokenId', sortDir = 'asc' } = options;

  try {
    // Fetch tokens and count in parallel
    const sortParam = sortDir === 'desc' ? `sort.desc=${sort}` : `sort.asc=${sort}`;
    const [tokensResponse, countResponse] = await Promise.all([
      fetch(`${apiUrl}/tokens?contract=${contractAddress}&limit=${limit}&offset=${offset}&${sortParam}`),
      fetch(`${apiUrl}/tokens/count?contract=${contractAddress}`),
    ]);

    if (!tokensResponse.ok) {
      throw new Error(`TzKT API error: ${tokensResponse.status}`);
    }

    const tokens: TzktToken[] = await tokensResponse.json();
    const totalCount = countResponse.ok ? parseInt(await countResponse.text()) : undefined;

    const collection = getCollectionByAddress(contractAddress);

    const items: CollectionToken[] = tokens.map(token => ({
      tokenAddress: token.contract.address,
      tokenId: parseInt(token.tokenId),
      owner: '', // Will be fetched separately if needed
      metadata: mapTzktMetadata(token.metadata),
      collectionSlug: collection?.slug,
    }));

    return {
      items,
      pagination: {
        offset,
        limit,
        hasMore: tokens.length === limit,
        totalCount,
      },
    };
  } catch (error) {
    console.error('Failed to fetch collection tokens:', error);
    return {
      items: [],
      pagination: { offset, limit, hasMore: false },
    };
  }
};

/**
 * Fetch token count for a collection
 */
export const fetchCollectionTokenCount = async (
  contractAddress: string,
  network?: 'mainnet' | 'ghostnet' | 'sandbox'
): Promise<number> => {
  const apiUrl = getTzktApiUrl(network);

  try {
    const response = await fetch(`${apiUrl}/tokens/count?contract=${contractAddress}`);
    if (!response.ok) return 0;
    return parseInt(await response.text());
  } catch {
    return 0;
  }
};

/**
 * Fetch collection statistics
 */
export const fetchCollectionStats = async (
  contractAddress: string,
  network?: 'mainnet' | 'ghostnet' | 'sandbox'
): Promise<CollectionStats> => {
  const apiUrl = getTzktApiUrl(network);

  try {
    const [tokenCount, holdersResponse] = await Promise.all([
      fetchCollectionTokenCount(contractAddress, network),
      fetch(`${apiUrl}/tokens/balances/count?token.contract=${contractAddress}&balance.gt=0`),
    ]);

    const holderCount = holdersResponse.ok ? parseInt(await holdersResponse.text()) : 0;

    return {
      address: contractAddress,
      tokenCount,
      holderCount,
      lastUpdated: new Date(),
    };
  } catch (error) {
    console.error('Failed to fetch collection stats:', error);
    return {
      address: contractAddress,
      tokenCount: 0,
      holderCount: 0,
      lastUpdated: new Date(),
    };
  }
};

/**
 * Fetch tokens by specific IDs (batch lookup)
 */
export const fetchTokensByIds = async (
  contractAddress: string,
  tokenIds: number[]
): Promise<CollectionToken[]> => {
  if (tokenIds.length === 0) return [];

  const apiUrl = getTzktApiUrl();
  const idsParam = tokenIds.join(',');

  try {
    const response = await fetch(
      `${apiUrl}/tokens?contract=${contractAddress}&tokenId.in=${idsParam}&limit=${tokenIds.length}`
    );

    if (!response.ok) {
      throw new Error(`TzKT API error: ${response.status}`);
    }

    const tokens: TzktToken[] = await response.json();
    const collection = getCollectionByAddress(contractAddress);

    return tokens.map(token => ({
      tokenAddress: token.contract.address,
      tokenId: parseInt(token.tokenId),
      owner: '',
      metadata: mapTzktMetadata(token.metadata),
      collectionSlug: collection?.slug,
    }));
  } catch (error) {
    console.error('Failed to fetch tokens by IDs:', error);
    return [];
  }
};

/**
 * Fetch owners for multiple tokens
 */
export const fetchTokenOwners = async (
  contractAddress: string,
  tokenIds: number[]
): Promise<Map<number, string>> => {
  const ownerMap = new Map<number, string>();
  if (tokenIds.length === 0) return ownerMap;

  const apiUrl = getTzktApiUrl();
  const idsParam = tokenIds.join(',');

  try {
    const response = await fetch(
      `${apiUrl}/tokens/balances?token.contract=${contractAddress}&token.tokenId.in=${idsParam}&balance.gt=0&limit=${tokenIds.length}`
    );

    if (!response.ok) return ownerMap;

    const balances: TzktTokenBalance[] = await response.json();
    balances.forEach(balance => {
      ownerMap.set(parseInt(balance.token.tokenId), balance.account.address);
    });

    return ownerMap;
  } catch (error) {
    console.error('Failed to fetch token owners:', error);
    return ownerMap;
  }
};

/**
 * Build trait index for a collection (for filtering/rarity)
 * This fetches ALL tokens - use with caching!
 */
export const buildCollectionTraitIndex = async (
  contractAddress: string,
  onProgress?: (loaded: number, total: number) => void
): Promise<TraitIndex> => {
  const apiUrl = getTzktApiUrl();
  const traitIndex: TraitIndex = {};
  let offset = 0;
  const limit = 100;
  let totalCount = 0;
  let loadedCount = 0;

  try {
    // Get total count first
    const countResponse = await fetch(`${apiUrl}/tokens/count?contract=${contractAddress}`);
    if (countResponse.ok) {
      totalCount = parseInt(await countResponse.text());
    }

    // Fetch all tokens in batches
    while (true) {
      const response = await fetch(
        `${apiUrl}/tokens?contract=${contractAddress}&limit=${limit}&offset=${offset}&select=tokenId,metadata`
      );

      if (!response.ok) break;

      const tokens: Array<{ tokenId: string; metadata?: TzktTokenMetadata }> = await response.json();
      if (tokens.length === 0) break;

      // Process traits
      tokens.forEach(token => {
        if (token.metadata?.attributes) {
          token.metadata.attributes.forEach(attr => {
            const traitData = traitIndex[attr.name] as Record<string, number> | undefined;
            if (!traitData) {
              traitIndex[attr.name] = {};
            }
            const counts = traitIndex[attr.name] as Record<string, number>;
            const value = String(attr.value);
            counts[value] = (counts[value] || 0) + 1;
          });
        }
      });

      loadedCount += tokens.length;
      onProgress?.(loadedCount, totalCount);

      if (tokens.length < limit) break;
      offset += limit;
    }

    // Add metadata
    traitIndex._meta = {
      totalSupply: totalCount,
      buildTime: Date.now(),
    };

    return traitIndex;
  } catch (error) {
    console.error('Failed to build trait index:', error);
    return traitIndex;
  }
};

/**
 * Convert trait index to TraitSummary array for UI
 */
export const traitIndexToSummary = (traitIndex: TraitIndex): TraitSummary[] => {
  const totalSupply = traitIndex._meta?.totalSupply || 1;
  const summaries: TraitSummary[] = [];

  for (const [traitName, values] of Object.entries(traitIndex)) {
    if (traitName === '_meta') continue;

    const traitValues = Object.entries(values as Record<string, number>)
      .map(([value, count]) => ({
        value,
        count,
        percentage: Math.round((count / totalSupply) * 1000) / 10,
      }))
      .sort((a, b) => b.count - a.count);

    summaries.push({
      name: traitName,
      values: traitValues,
    });
  }

  return summaries.sort((a, b) => a.name.localeCompare(b.name));
};

// In-memory cache for trait indexes (survives page navigation within SPA)
const traitIndexMemoryCache = new Map<string, TraitIndex>();

// Track in-progress builds to prevent concurrent fetches
const traitIndexBuildingPromises = new Map<string, Promise<TraitIndex>>();

// Cache TTL: 24 hours for localStorage, memory cache lives until page refresh
const TRAIT_CACHE_TTL = 24 * 60 * 60 * 1000;

/**
 * Get cached trait index from memory/localStorage or build new one
 * Uses multiple cache layers:
 * 1. In-memory cache (fastest, cleared on page refresh)
 * 2. localStorage (persists across sessions, 24h TTL)
 * 3. Build from API (slowest, only when cache misses)
 */
export const getOrBuildTraitIndex = async (
  contractAddress: string,
  onProgress?: (loaded: number, total: number) => void
): Promise<TraitIndex> => {
  const cacheKey = `trait_index_${contractAddress}`;

  // Layer 1: Check in-memory cache first (instant)
  const memCached = traitIndexMemoryCache.get(cacheKey);
  if (memCached) {
    console.log(`[TraitCache] Memory hit for ${contractAddress.slice(0, 10)}...`);
    return memCached;
  }

  // Check if already building (prevent concurrent builds)
  const existingBuild = traitIndexBuildingPromises.get(cacheKey);
  if (existingBuild) {
    console.log(`[TraitCache] Waiting for in-progress build for ${contractAddress.slice(0, 10)}...`);
    return existingBuild;
  }

  // Layer 2: Check localStorage cache
  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const parsed: TraitIndex = JSON.parse(cached);
      if (parsed._meta && Date.now() - parsed._meta.buildTime < TRAIT_CACHE_TTL) {
        console.log(`[TraitCache] localStorage hit for ${contractAddress.slice(0, 10)}...`);
        // Populate memory cache for faster subsequent access
        traitIndexMemoryCache.set(cacheKey, parsed);
        return parsed;
      }
      console.log(`[TraitCache] localStorage expired for ${contractAddress.slice(0, 10)}...`);
    }
  } catch {
    // Cache miss or invalid JSON
  }

  // Layer 3: Build new index from API
  console.log(`[TraitCache] Building index for ${contractAddress.slice(0, 10)}...`);

  const buildPromise = buildCollectionTraitIndex(contractAddress, onProgress);
  traitIndexBuildingPromises.set(cacheKey, buildPromise);

  try {
    const index = await buildPromise;

    // Store in memory cache
    traitIndexMemoryCache.set(cacheKey, index);

    // Store in localStorage
    try {
      localStorage.setItem(cacheKey, JSON.stringify(index));
      console.log(`[TraitCache] Cached ${contractAddress.slice(0, 10)}... (${Object.keys(index).length - 1} traits)`);
    } catch (e) {
      console.warn('[TraitCache] localStorage full, using memory cache only');
    }

    return index;
  } finally {
    traitIndexBuildingPromises.delete(cacheKey);
  }
};

/**
 * Clear trait cache for a specific collection or all collections
 */
export const clearTraitCache = (contractAddress?: string): void => {
  if (contractAddress) {
    const cacheKey = `trait_index_${contractAddress}`;
    traitIndexMemoryCache.delete(cacheKey);
    localStorage.removeItem(cacheKey);
  } else {
    // Clear all trait caches
    traitIndexMemoryCache.clear();
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('trait_index_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  }
};

/**
 * Fetch NFTs owned by address filtered to a specific collection
 */
export const fetchOwnedCollectionNFTs = async (
  contractAddress: string,
  ownerAddress: string
): Promise<CollectionToken[]> => {
  const apiUrl = getTzktApiUrl();

  try {
    const response = await fetch(
      `${apiUrl}/tokens/balances?account=${ownerAddress}&token.contract=${contractAddress}&balance.gt=0&limit=100`
    );

    if (!response.ok) {
      throw new Error(`TzKT API error: ${response.status}`);
    }

    const balances: TzktTokenBalance[] = await response.json();

    return balances.map(balance => {
      const collection = getCollectionByAddress(balance.token.contract.address);
      return {
        tokenAddress: balance.token.contract.address,
        tokenId: balance.token.tokenId, // Keep as string to preserve large integer precision
        owner: balance.account.address,
        metadata: mapTzktMetadata(balance.token.metadata),
        collectionSlug: collection?.slug,
      };
    });
  } catch (error) {
    console.error('Failed to fetch owned collection NFTs:', error);
    return [];
  }
};

// ============================================================================
// Sandbox/Development Mode Functions
// ============================================================================

// Cached manifest for sandbox mode (loaded once, used for filtering)
interface SandboxManifest {
  name: string;
  count: number;
  tokens: Array<{
    id: number;
    attributes: Array<{ name: string; value: string }>;
    imageUri: string;
    metadataUri: string;
  }>;
}
let sandboxManifestCache: SandboxManifest | null = null;
let sandboxManifestPromise: Promise<SandboxManifest | null> | null = null;

/**
 * Load sandbox manifest from public folder (cached)
 */
export const loadSandboxManifest = async (): Promise<SandboxManifest | null> => {
  if (sandboxManifestCache) {
    return sandboxManifestCache;
  }

  if (sandboxManifestPromise) {
    return sandboxManifestPromise;
  }

  sandboxManifestPromise = (async () => {
    try {
      const response = await fetch('/sandbox-manifest.json');
      if (response.ok) {
        const manifest = await response.json() as SandboxManifest;
        sandboxManifestCache = manifest;
        console.log('[loadSandboxManifest] Loaded manifest with', manifest.tokens.length, 'tokens');
        return manifest;
      }
    } catch (e) {
      console.warn('[loadSandboxManifest] Failed to load manifest:', e);
    }
    return null;
  })();

  return sandboxManifestPromise;
};

/**
 * Fetch tokens directly from FA2 contract storage via Taquito (for sandbox mode)
 * This bypasses TzKT which may not be indexing the local sandbox
 */
export const fetchSandboxCollectionTokens = async (
  contractAddress: string,
  options: {
    offset?: number;
    limit?: number;
  } = {}
): Promise<PaginatedResponse<CollectionToken>> => {
  const { offset = 0, limit = 24 } = options;
  const rpcUrl = getRpcUrl();

  try {
    const { TezosToolkit } = await import('@taquito/taquito');
    const tezos = new TezosToolkit(rpcUrl);
    const contract = await tezos.contract.at(contractAddress);
    const storage = await contract.storage() as {
      next_token_id: { toNumber: () => number };
      ledger: { get: (id: number) => Promise<string | undefined> };
      token_metadata: { get: (id: number) => Promise<{ token_id: { toNumber: () => number }; token_info: Map<string, string> } | undefined> };
    };

    const totalCount = storage.next_token_id.toNumber();
    const collection = getCollectionByAddress(contractAddress);
    const items: CollectionToken[] = [];

    // Fetch tokens in the requested range
    const start = offset;
    const end = Math.min(offset + limit, totalCount);

    for (let tokenId = start; tokenId < end; tokenId++) {
      try {
        const owner = await storage.ledger.get(tokenId);
        const tokenMetaEntry = await storage.token_metadata.get(tokenId);

        let metadata: NFTMetadata = {
          name: `Token #${tokenId}`,
          description: '',
          image: '',
        };

        // Parse token metadata from big_map
        if (tokenMetaEntry?.token_info) {
          const tokenInfo = tokenMetaEntry.token_info;
          // The empty string key "" contains the metadata URI
          const metadataUriHex = tokenInfo.get('');
          if (metadataUriHex) {
            // Decode hex to string (browser-compatible)
            const metadataUri = hexToUtf8(metadataUriHex);

            // Fetch metadata from IPFS
            if (metadataUri.startsWith('ipfs://')) {
              try {
                const metadataJson = await fetchIPFSMetadata(metadataUri);
                if (metadataJson) {
                  metadata = {
                    name: metadataJson.name || metadata.name,
                    description: metadataJson.description || '',
                    image: metadataJson.displayUri || metadataJson.artifactUri || metadataJson.thumbnailUri || metadataJson.image || '',
                    attributes: metadataJson.attributes?.map((attr: { name: string; value: string }) => ({
                      trait_type: attr.name,
                      value: attr.value,
                    })),
                  };
                }
              } catch (e) {
                console.warn(`Failed to fetch metadata for token ${tokenId}:`, e);
              }
            }
          }
        }

        items.push({
          tokenAddress: contractAddress,
          tokenId: String(tokenId),
          owner: owner || '',
          metadata,
          collectionSlug: collection?.slug,
        });
      } catch (e) {
        console.warn(`Failed to fetch token ${tokenId}:`, e);
      }
    }

    return {
      items,
      pagination: {
        offset,
        limit,
        hasMore: end < totalCount,
        totalCount,
      },
    };
  } catch (error) {
    console.error('Failed to fetch sandbox collection tokens:', error);
    return {
      items: [],
      pagination: { offset, limit, hasMore: false },
    };
  }
};

/**
 * Fetch sandbox NFTs for the Explore page
 * Returns NFTs from the test FA2 collection
 */
export const fetchSandboxNFTs = async (fa2Address: string): Promise<NFT[]> => {
  const result = await fetchSandboxCollectionTokens(fa2Address, { offset: 0, limit: 50 });
  return result.items.map(token => ({
    tokenAddress: token.tokenAddress,
    tokenId: token.tokenId,
    owner: token.owner,
    metadata: token.metadata,
  }));
};

/**
 * Fetch all tokens from sandbox and compute traits
 * This is a simplified version that fetches all tokens and computes traits client-side
 */
export const fetchSandboxTraits = async (
  contractAddress: string
): Promise<{ traits: TraitSummary[]; totalSupply: number }> => {
  const rpcUrl = getRpcUrl();
  console.log('[fetchSandboxTraits] Starting with contract:', contractAddress, 'rpcUrl:', rpcUrl);

  try {
    // Get total supply from contract
    const { TezosToolkit } = await import('@taquito/taquito');
    const tezos = new TezosToolkit(rpcUrl);
    const contract = await tezos.contract.at(contractAddress);
    const storage = await contract.storage() as {
      next_token_id: { toNumber: () => number };
    };
    const totalCount = storage.next_token_id.toNumber();
    console.log('[fetchSandboxTraits] Total token count:', totalCount);

    // Try to load pre-computed traits from public folder
    try {
      const response = await fetch('/sandbox-traits.json');
      if (response.ok) {
        const distribution = await response.json() as Record<string, Record<string, number>>;
        console.log('[fetchSandboxTraits] Loaded pre-computed traits from sandbox-traits.json');

        // Convert distribution format to TraitSummary format
        const traits: TraitSummary[] = Object.entries(distribution).map(([name, values]) => ({
          name,
          values: Object.entries(values)
            .map(([value, count]) => ({
              value,
              count,
              percentage: Math.round((count / totalCount) * 1000) / 10,
            }))
            .sort((a, b) => b.count - a.count),
        })).sort((a, b) => a.name.localeCompare(b.name));

        return { traits, totalSupply: totalCount };
      }
    } catch (e) {
      console.warn('[fetchSandboxTraits] Could not load pre-computed traits:', e);
    }

    // Fallback: return empty traits
    return { traits: [], totalSupply: totalCount };
  } catch (error) {
    console.error('Failed to fetch sandbox traits:', error);
    return { traits: [], totalSupply: 0 };
  }
};

/**
 * Fetch filtered tokens from sandbox collection
 * Uses pre-computed manifest for fast client-side filtering
 */
export const fetchSandboxFilteredTokens = async (
  contractAddress: string,
  options: {
    offset?: number;
    limit?: number;
    sort?: string;
    traits?: Record<string, string[]>;
  } = {}
): Promise<PaginatedResponse<CollectionToken>> => {
  const { offset = 0, limit = 24, sort = 'price_asc', traits } = options;
  console.log('[fetchSandboxFilteredTokens] Starting with contract:', contractAddress, 'offset:', offset, 'limit:', limit, 'sort:', sort, 'traits:', traits);

  const collection = getCollectionByAddress(contractAddress);

  // Try to load manifest for fast filtering
  const manifest = await loadSandboxManifest();

  if (manifest) {
    // Use manifest for filtering (no IPFS fetches needed)
    let filteredTokens = manifest.tokens;

    // Apply trait filters if specified
    if (traits && Object.keys(traits).length > 0) {
      filteredTokens = filteredTokens.filter(token => {
        // Token must match ALL selected trait categories
        for (const [traitName, selectedValues] of Object.entries(traits)) {
          if (selectedValues.length === 0) continue;

          // Find this trait in the token's attributes
          const tokenTrait = token.attributes.find(attr => attr.name === traitName);

          // If token doesn't have this trait, check if "None" is selected
          if (!tokenTrait) {
            if (!selectedValues.includes('None')) {
              return false;
            }
          } else {
            // Token has this trait - check if its value is in selected values
            if (!selectedValues.includes(tokenTrait.value)) {
              return false;
            }
          }
        }
        return true;
      });
      console.log('[fetchSandboxFilteredTokens] Filtered to', filteredTokens.length, 'tokens');
    }

    // Fetch marketplace listings (for both sorting and displaying price)
    const listings = await fetchMarketplaceListings();
    const listingMap = new Map<number, NFTListing>(); // tokenId -> listing

    // Build lookup map for tokens in this collection
    for (const listing of listings) {
      if (listing.tokenAddress === contractAddress && listing.active) {
        listingMap.set(Number(listing.tokenId), listing);
      }
    }

    console.log('[fetchSandboxFilteredTokens] Found', listingMap.size, 'listings for collection');

    // Apply sorting
    if (sort === 'price_asc' || sort === 'price_desc') {
      // Sort: listed items first (by price), then unlisted items (by token ID)
      filteredTokens = [...filteredTokens].sort((a, b) => {
        const listingA = listingMap.get(a.id);
        const listingB = listingMap.get(b.id);
        const priceA = listingA ? Number(listingA.price) : undefined;
        const priceB = listingB ? Number(listingB.price) : undefined;

        // Both listed - sort by price
        if (priceA !== undefined && priceB !== undefined) {
          return sort === 'price_asc' ? priceA - priceB : priceB - priceA;
        }

        // Only one listed - listed item comes first
        if (priceA !== undefined) return -1;
        if (priceB !== undefined) return 1;

        // Neither listed - sort by token ID
        return a.id - b.id;
      });
    } else if (sort === 'tokenId_desc') {
      filteredTokens = [...filteredTokens].sort((a, b) => b.id - a.id);
    }
    // Default: tokenId_asc (already sorted by ID in manifest)

    const totalCount = filteredTokens.length;

    // Apply pagination
    const paginatedTokens = filteredTokens.slice(offset, offset + limit);

    // Convert manifest tokens to CollectionToken format (include listing if available)
    const items: CollectionToken[] = paginatedTokens.map(token => {
      const listing = listingMap.get(token.id);
      return {
        tokenAddress: contractAddress,
        tokenId: String(token.id),
        owner: '', // Not tracked in manifest
        metadata: {
          name: `${manifest.name} #${token.id}`,
          description: '',
          image: token.imageUri,
          attributes: token.attributes.map(attr => ({
            trait_type: attr.name,
            value: attr.value,
          })),
        },
        collectionSlug: collection?.slug,
        listing: listing,
      };
    });

    return {
      items,
      pagination: {
        offset,
        limit,
        hasMore: offset + limit < totalCount,
        totalCount,
      },
    };
  }

  // Fallback: fetch from contract (slower, no filtering support)
  console.log('[fetchSandboxFilteredTokens] No manifest, falling back to contract fetch');
  const rpcUrl = getRpcUrl();
  const { TezosToolkit } = await import('@taquito/taquito');
  const tezos = new TezosToolkit(rpcUrl);
  const contract = await tezos.contract.at(contractAddress);
  const storage = await contract.storage() as {
    next_token_id: { toNumber: () => number };
    ledger: { get: (id: number) => Promise<string | undefined> };
    token_metadata: { get: (id: number) => Promise<{ token_id: { toNumber: () => number }; token_info: Map<string, string> } | undefined> };
  };

  const totalCount = storage.next_token_id.toNumber();
  const tokens: CollectionToken[] = [];

  // Only fetch the requested page of tokens
  const startId = offset;
  const endId = Math.min(offset + limit, totalCount);
  console.log('[fetchSandboxFilteredTokens] Fetching tokens', startId, 'to', endId, 'of', totalCount);

  // Batch fetch tokens in parallel (groups of 5 to avoid overwhelming IPFS)
  const batchSize = 5;
  for (let i = startId; i < endId; i += batchSize) {
    const batchEnd = Math.min(i + batchSize, endId);
    const batchPromises = [];

    for (let tokenId = i; tokenId < batchEnd; tokenId++) {
      batchPromises.push((async () => {
        try {
          const owner = await storage.ledger.get(tokenId);
          const tokenMetaEntry = await storage.token_metadata.get(tokenId);

          let metadata: NFTMetadata = {
            name: `Token #${tokenId}`,
            description: '',
            image: '',
          };

          if (tokenMetaEntry?.token_info) {
            const metadataUriHex = tokenMetaEntry.token_info.get('');
            if (metadataUriHex) {
              const metadataUri = hexToUtf8(metadataUriHex);
              if (metadataUri.startsWith('ipfs://')) {
                try {
                  const metadataJson = await fetchIPFSMetadata(metadataUri);
                  if (metadataJson) {
                    metadata = {
                      name: metadataJson.name || metadata.name,
                      description: metadataJson.description || '',
                      image: metadataJson.displayUri || metadataJson.artifactUri || metadataJson.thumbnailUri || metadataJson.image || '',
                      attributes: metadataJson.attributes?.map((attr: { name: string; value: string }) => ({
                        trait_type: attr.name,
                        value: attr.value,
                      })),
                    };
                  }
                } catch (e) {
                  // Skip metadata fetch errors
                }
              }
            }
          }

          return {
            tokenAddress: contractAddress,
            tokenId: String(tokenId),
            owner: owner || '',
            metadata,
            collectionSlug: collection?.slug,
          } as CollectionToken;
        } catch (e) {
          return null;
        }
      })());
    }

    const batchResults = await Promise.all(batchPromises);
    tokens.push(...batchResults.filter((t): t is CollectionToken => t !== null));
  }

  return {
    items: tokens,
    pagination: {
      offset,
      limit,
      hasMore: offset + limit < totalCount,
      totalCount,
    },
  };
};
